import React, { useState, useEffect } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import {
  Container,
  Typography,
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TableSortLabel,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Alert,
  CircularProgress,
  Grid,
  Card,
  CardMedia,
  CardContent,
  Fab,
  LinearProgress,
  Tooltip,
  OutlinedInput,
  ListItemText,
  Checkbox,
  InputAdornment,
  Stack
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  CloudUpload as CloudUploadIcon,
  Image as ImageIcon,
  Category as CategoryIcon,
  Search,
  FilterList,
  ArrowUpward,
  ArrowDownward,
  Refresh
} from '@mui/icons-material';
import { productService, adminService } from '../../services/api';
import uploadService from '../../services/uploadService';
import { toast } from 'react-toastify';
import { COLORS } from '../../theme/colors';

const AdminProducts = () => {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [allCategories, setAllCategories] = useState([]); // Flat list for selection
  const [loading, setLoading] = useState(true);
  
  // Pagination, Search, Filter & Sort state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [stockFilter, setStockFilter] = useState('all');
  const [templateFilter, setTemplateFilter] = useState('all');
  const [sortBy, setSortBy] = useState('name');
  const [sortDirection, setSortDirection] = useState('asc');
  
  const [openDialog, setOpenDialog] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [uploadingImages, setUploadingImages] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [previewImages, setPreviewImages] = useState([]);
  
  // Bulk selection state
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [bulkLoading, setBulkLoading] = useState(false);
  
  // (Categories management moved to dedicated page)
  
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    shortDescription: '',
    longDescription: '',
    sku: '',
    price: '',
    salePrice: '', // New field for discounted price
    category: '', // Keep for backward compatibility
    categories: [], // New field for multiple categories
    imageUrls: [],
    inStock: true,
    featured: false, // New field for featured products
    label: '', // New field for product labels (e.g., "New", "Sale", "Limited")
    imageRequired: 0,
    textRequired: false,
    customizationOptions: {},
    templateType: 'standard',
    youtubeUrl: '', // YouTube video URL
    hsnSacCode: '', // Add missing HSN/SAC code field
    shipping: {
      weight: '',
      dimensions: {
        length: '',
        width: '',
        height: ''
      }
    }
  });

  useEffect(() => {
    loadProducts();
    loadCategories(); // still load categories for product assignment
  }, []);

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
      setPage(0); // Reset to first page when searching
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Reset selection when products change
  useEffect(() => {
    setSelectedProducts([]);
    setSelectAll(false);
  }, [products]);

  const loadProducts = async () => {
    try {
      setLoading(true);
      const response = await productService.getAllProductsAdmin();
      setProducts(response.data.products || []);
    } catch (error) {
      console.error('Error loading products:', error);
      toast.error('Failed to load products');
    } finally {
      setLoading(false);
    }
  };

  const loadCategories = async () => {
    try {
      const response = await adminService.getCategories();
      if (response.data && Array.isArray(response.data)) {
        setCategories(response.data);
        setAllCategories(response.data); // flat list used throughout
      } else {
        setCategories([]);
        setAllCategories([]);
      }
    } catch (error) {
      console.error('Error loading categories:', error);
      setCategories([]);
      setAllCategories([]);
    }
  };

  // Category management removed (now standalone page)
  // (Force delete / reassignment logic removed)

  // tabs removed

  const handleOpenDialog = (product = null) => {
    if (product) {
      setEditingProduct(product);
      setFormData({
        name: product.name || '',
        description: product.description || '',
        shortDescription: product.shortDescription || product.description || '',
        longDescription: product.longDescription || product.description || '',
        sku: product.sku || '',
        price: product.price || '',
        salePrice: product.salePrice || '',
        category: product.category || '',
        categories: product.categories || (product.category ? [product.category] : []),
        imageUrls: product.imageUrls || [],
        inStock: product.active !== undefined ? product.active : (product.inStock !== undefined ? product.inStock : true),
        featured: product.featured || false,
        label: product.label || '',
        imageRequired: product.imageRequired || 0,
        textRequired: product.textRequired || false,
        customizationOptions: product.customizationOptions || {},
        templateType: product.templateType || 'standard',
        youtubeUrl: product.youtubeUrl || '',
        hsnSacCode: product.hsnSacCode || '',
        shipping: {
          weight: product.shipping?.weight || '',
          dimensions: {
            length: product.shipping?.dimensions?.length || '',
            width: product.shipping?.dimensions?.width || '',
            height: product.shipping?.dimensions?.height || ''
          }
        }
      });
      setPreviewImages(product.imageUrls || []);
    } else {
      setEditingProduct(null);
      setFormData({
        name: '',
        description: '',
        shortDescription: '',
        longDescription: '',
        sku: '',
        price: '',
        salePrice: '',
        category: '',
        categories: [],
        imageUrls: [],
        inStock: true,
        featured: false,
        label: '',
        imageRequired: 0,
        textRequired: false,
        customizationOptions: {},
        templateType: 'standard',
        youtubeUrl: '',
        hsnSacCode: '',
        shipping: {
          weight: '',
          dimensions: {
            length: '',
            width: '',
            height: ''
          }
        }
      });
      setPreviewImages([]);
    }
    setSelectedFiles([]);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingProduct(null);
    setSelectedFiles([]);
    setPreviewImages([]);
    setUploadProgress(0);
  };

  const handleFileSelect = (event) => {
    const files = Array.from(event.target.files);
    if (files.length > 0) {
      // Validate files
      const validFiles = files.filter(file => {
        try {
          return uploadService.validateFile(file);
        } catch (error) {
          console.error('File validation error:', error);
          return false;
        }
      });
      setSelectedFiles(prevFiles => [...prevFiles, ...validFiles]);
    }
  };

  const handleRemoveSelectedFile = (index) => {
    setSelectedFiles(prevFiles => prevFiles.filter((_, i) => i !== index));
  };

  const handleRemoveExistingImage = (index) => {
    const newImages = previewImages.filter((_, i) => i !== index);
    setPreviewImages(newImages);
    setFormData(prev => ({
      ...prev,
      imageUrls: newImages
    }));
  };

  const handleMoveImage = (fromIndex, toIndex) => {
    const newImages = [...previewImages];
    const [movedImage] = newImages.splice(fromIndex, 1);
    newImages.splice(toIndex, 0, movedImage);
    setPreviewImages(newImages);
    setFormData(prev => ({
      ...prev,
      imageUrls: newImages
    }));
  };

  // Helper function to get valid template types based on image and text requirements
  const getValidTemplateTypes = (imageRequired, textRequired) => {
    const imageReq = parseInt(imageRequired) || 0;
    
    if (imageReq === 0 && !textRequired) {
      return ['standard']; // Only standard when no image and no text
    } else if (imageReq === 0 && textRequired) {
      return ['standard', 'text-only']; // Standard or text-only when no image but text required
    } else if (imageReq === 1 && !textRequired) {
      return ['standard', 'single-image']; // Standard or single-image when 1 image, no text
    } else if (imageReq === 1 && textRequired) {
      return ['standard', 'single-image', 'image-and-text']; // Standard, single-image, or image+text
    } else if (imageReq > 1 && !textRequired) {
      return ['standard', 'multi-image']; // Standard or multi-image when multiple images, no text
    } else if (imageReq > 1 && textRequired) {
      return ['standard', 'multi-image', 'image-and-text']; // Standard, multi-image, or image+text
    }
    
    return ['standard']; // Fallback
  };

  // Helper function to get the best template type when constraints change
  const getOptimalTemplateType = (imageRequired, textRequired, currentTemplate) => {
    const validTypes = getValidTemplateTypes(imageRequired, textRequired);
    
    // If current template is still valid, keep it
    if (validTypes.includes(currentTemplate)) {
      return currentTemplate;
    }
    
    // Otherwise, suggest the most appropriate template
    const imageReq = parseInt(imageRequired) || 0;
    
    if (imageReq === 0 && textRequired) {
      return 'text-only';
    } else if (imageReq === 1 && textRequired) {
      return 'image-and-text';
    } else if (imageReq === 1 && !textRequired) {
      return 'single-image';
    } else if (imageReq > 1 && textRequired) {
      return 'image-and-text';
    } else if (imageReq > 1 && !textRequired) {
      return 'multi-image';
    }
    
    return 'standard';
  };

  // Handler for shipping-related fields
  const handleShippingChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      shipping: {
        ...prev.shipping,
        [field]: value
      }
    }));
  };

  const handleDimensionChange = (dimension, value) => {
    setFormData(prev => ({
      ...prev,
      shipping: {
        ...prev.shipping,
        dimensions: {
          ...prev.shipping.dimensions,
          [dimension]: value
        }
      }
    }));
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => {
      let newData = {
        ...prev,
        [field]: value
      };

      // Handle interconnected logic
      if (field === 'imageRequired') {
        const imageReq = parseInt(value) || 0;
        
        // Auto-adjust template type based on new image requirement
        const newTemplateType = getOptimalTemplateType(imageReq, prev.textRequired, prev.templateType);
        newData.templateType = newTemplateType;
        
        // If image requirement is 0, ensure text is enabled for meaningful templates
        if (imageReq === 0 && !prev.textRequired && newTemplateType === 'text-only') {
          newData.textRequired = true;
        }
      }

      if (field === 'textRequired') {
        // Auto-adjust template type based on new text requirement
        const newTemplateType = getOptimalTemplateType(prev.imageRequired, value, prev.templateType);
        newData.templateType = newTemplateType;
      }

      if (field === 'templateType') {
        // Validate and auto-adjust other fields based on template type
        const imageReq = parseInt(prev.imageRequired) || 0;
        
        switch (value) {
          case 'text-only':
            newData.imageRequired = 0;
            newData.textRequired = true;
            break;
          case 'single-image':
            if (imageReq === 0) newData.imageRequired = 1;
            if (imageReq > 1) newData.imageRequired = 1;
            break;
          case 'multi-image':
            if (imageReq <= 1) newData.imageRequired = 2;
            break;
          case 'image-and-text':
            newData.textRequired = true;
            if (imageReq === 0) newData.imageRequired = 1;
            break;
          case 'standard':
            // Standard can work with any combination, no forced changes
            break;
        }
      }

      return newData;
    });
  };

  const handleImageUrlChange = (index, value) => {
    const newImageUrls = [...formData.imageUrls];
    newImageUrls[index] = value;
    setFormData(prev => ({
      ...prev,
      imageUrls: newImageUrls
    }));
  };

  const addImageUrlField = () => {
    setFormData(prev => ({
      ...prev,
      imageUrls: [...prev.imageUrls, '']
    }));
  };

  const removeImageUrlField = (index) => {
    if (formData.imageUrls.length > 1) {
      const newImageUrls = formData.imageUrls.filter((_, i) => i !== index);
      setFormData(prev => ({
        ...prev,
        imageUrls: newImageUrls
      }));
    }
  };

  const generateProductId = (name) => {
    return name.toLowerCase()
      .replace(/[^a-z0-9\s]/g, '')
      .replace(/\s+/g, '-')
      + '-' + Date.now().toString().slice(-6);
  };

  const updateCustomizationOptions = () => {
    let options = {};
    
    if (formData.textRequired) {
      options.textField = {
        label: 'Custom Text',
        placeholder: 'Enter your text',
        maxLength: 50
      };
    }
    
    if (formData.imageRequired > 0) {
      options.imageUpload = {
        label: `Upload ${formData.imageRequired} Photo${formData.imageRequired > 1 ? 's' : ''}`,
        acceptedFormats: ['jpg', 'jpeg', 'png'],
        maxSize: '5MB'
      };
    }
    
    return options;
  };

  // Helper function to get category name by ID
  const getCategoryName = (categoryId) => {
    const category = allCategories.find(cat => cat.id === categoryId);
    return category ? category.name : categoryId;
  };

  // Helper function to get category path display
  const getCategoryPath = (categoryId) => {
    const category = allCategories.find(cat => cat.id === categoryId);
    if (!category) return categoryId;
    
    if (category.parentId) {
      const parent = allCategories.find(cat => cat.id === category.parentId);
      if (parent) {
        return `${parent.name} → ${category.name}`;
      }
    }
    
    return category.name;
  };

  // Handle multiple category selection
  const handleCategoryChange = (event) => {
    const value = event.target.value;
    setFormData(prev => ({
      ...prev,
      categories: typeof value === 'string' ? value.split(',') : value,
      category: value.length > 0 ? value[0] : '' // Keep backward compatibility
    }));
  };

  // Generate SKU automatically if not provided
  const generateSKU = (name, existingSku = '') => {
    if (existingSku.trim()) return existingSku; // Keep existing SKU if provided
    
    // Generate SKU from product name
    const cleanName = name
      .replace(/[^a-zA-Z0-9\s]/g, '') // Remove special characters
      .trim()
      .toUpperCase()
      .split(' ')
      .slice(0, 3) // Take first 3 words max
      .join('-');
    
    // Add timestamp suffix to ensure uniqueness
    const timestamp = Date.now().toString().slice(-4);
    return `${cleanName}-${timestamp}`;
  };

  // Check for duplicate SKU
  const checkDuplicateSKU = (sku, currentProductId = null) => {
    if (!sku.trim()) return false;
    return products.some(product => 
      product.sku === sku && 
      product.id !== currentProductId && 
      product.docId !== currentProductId
    );
  };

  const handleSaveProduct = async () => {
    setLoading(true);
    
    try {
      // Generate SKU if not provided
      const finalSKU = generateSKU(formData.name, formData.sku);
      
      // Check for duplicate SKU
      if (checkDuplicateSKU(finalSKU, editingProduct?.docId || editingProduct?.id)) {
        toast.error('SKU already exists! Please use a different SKU.');
        setLoading(false);
        return;
      }
      
      let imageUrls = [...formData.imageUrls];
      
      // Upload new images if any are selected
      if (selectedFiles.length > 0) {
        setUploadingImages(true);
        try {
          const uploadedUrls = await uploadService.uploadProductImages(
            selectedFiles,
            formData.name,
            (progress) => setUploadProgress(progress)
          );
          imageUrls = [...imageUrls, ...uploadedUrls];
        } catch (error) {
          console.error('Error uploading images:', error);
          toast.error('Error uploading images. Please try again.');
          return;
        } finally {
          setUploadingImages(false);
          setUploadProgress(0);
        }
      }

      const productData = {
        ...formData,
        sku: finalSKU,
        price: parseFloat(formData.price),
        imageRequired: parseInt(formData.imageRequired),
        customizationOptions: updateCustomizationOptions(),
        imageUrls: imageUrls.filter(url => url.trim() !== ''),
        active: formData.inStock,  // Map inStock to active for backend
        categories: formData.categories.length > 0 ? formData.categories : [formData.category].filter(Boolean)
      };

      if (editingProduct) {
        // Update existing product - use docId for Firestore document ID
        await productService.updateProduct(editingProduct.docId, productData);
        toast.success('Product updated successfully!');
      } else {
        // Create new product
        const productId = generateProductId(formData.name);
        await productService.createProduct({ ...productData, id: productId });
        toast.success('Product created successfully!');
      }
      
      handleCloseDialog();
      loadProducts();
    } catch (error) {
      console.error('Error saving product:', error);
      toast.error('Failed to save product');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteProduct = async (productId) => {
    if (window.confirm('Are you sure you want to delete this product?')) {
      try {
        await productService.deleteProduct(productId);
        toast.success('Product deleted successfully!');
        // Reload products to get the updated list (since backend does soft delete)
        loadProducts();
      } catch (error) {
        console.error('Error deleting product:', error);
        toast.error('Failed to delete product');
      }
    }
  };

  // Bulk selection handlers
  const handleSelectProduct = (productId) => {
    setSelectedProducts(prev => {
      if (prev.includes(productId)) {
        return prev.filter(id => id !== productId);
      } else {
        return [...prev, productId];
      }
    });
  };

  const handleSelectAll = () => {
    if (selectAll || selectedProducts.length === sortedProducts.length) {
      setSelectedProducts([]);
      setSelectAll(false);
    } else {
      setSelectedProducts(sortedProducts.map(product => product.docId || product.id));
      setSelectAll(true);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedProducts.length === 0) {
      toast.error('Please select products to delete');
      return;
    }

    if (window.confirm(`Are you sure you want to delete ${selectedProducts.length} products?`)) {
      try {
        setBulkLoading(true);
        
        // Delete products one by one
        for (const productId of selectedProducts) {
          await productService.deleteProduct(productId);
        }
        
        toast.success(`${selectedProducts.length} products deleted successfully!`);
        setSelectedProducts([]);
        setSelectAll(false);
        loadProducts();
      } catch (error) {
        console.error('Error bulk deleting products:', error);
        toast.error('Failed to delete some products');
      } finally {
        setBulkLoading(false);
      }
    }
  };

  const handleBulkStockUpdate = async (inStock) => {
    if (selectedProducts.length === 0) {
      toast.error('Please select products to update');
      return;
    }

    try {
      setBulkLoading(true);
      
      // Update products one by one using correct document ID
      for (const productId of selectedProducts) {
        const product = products.find(p => (p.docId || p.id) === productId);
        if (product) {
          await productService.updateProduct(productId, { ...product, active: inStock, inStock });
        }
      }
      
      const statusText = inStock ? 'in stock' : 'out of stock';
      toast.success(`${selectedProducts.length} products marked as ${statusText}!`);
      setSelectedProducts([]);
      setSelectAll(false);
      loadProducts();
    } catch (error) {
      console.error('Error bulk updating stock:', error);
      toast.error('Failed to update some products');
    } finally {
      setBulkLoading(false);
    }
  };

  const getTemplateTypeColor = (templateType) => {
    switch (templateType) {
      case 'text-only': return 'primary';
      case 'single-image': return 'secondary';
      case 'multi-image': return 'warning';
      case 'image-and-text': return 'info';
      default: return 'default';
    }
  };

  // Helper functions for filtering and sorting
  const handleSort = (field) => {
    if (sortBy === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortDirection('asc');
    }
    setPage(0); // Reset to first page when sorting
  };

  // Filter and sort products
  const filteredProducts = products.filter(product => {
    // Category filter
    if (categoryFilter !== 'all') {
      const productCategories = product.categories || (product.category ? [product.category] : []);
      if (!productCategories.includes(categoryFilter)) {
        return false;
      }
    }
    
    // Stock filter
    if (stockFilter !== 'all') {
      const isInStock = product.active !== undefined ? product.active : product.inStock;
      if (stockFilter === 'inStock' && !isInStock) return false;
      if (stockFilter === 'outOfStock' && isInStock) return false;
    }
    
    // Template filter
    if (templateFilter !== 'all' && product.templateType !== templateFilter) {
      return false;
    }
    
    // Search query filter
    if (debouncedSearchQuery.trim()) {
      const query = debouncedSearchQuery.toLowerCase().trim();
      const searchableFields = [
        product.name,
        product.description,
        product.sku,
        product.category,
        ...(product.categories || []).map(catId => getCategoryPath(catId))
      ].filter(Boolean).join(' ').toLowerCase();
      
      if (!searchableFields.includes(query)) {
        return false;
      }
    }
    
    return true;
  });

  // Sort products
  const sortedProducts = [...filteredProducts].sort((a, b) => {
    let aValue, bValue;
    
    switch (sortBy) {
      case 'name':
        aValue = a.name.toLowerCase();
        bValue = b.name.toLowerCase();
        break;
      case 'sku':
        aValue = (a.sku || '').toLowerCase();
        bValue = (b.sku || '').toLowerCase();
        break;
      case 'price':
        aValue = parseFloat(a.price) || 0;
        bValue = parseFloat(b.price) || 0;
        break;
      case 'category':
        aValue = getCategoryPath((a.categories && a.categories[0]) || a.category || '').toLowerCase();
        bValue = getCategoryPath((b.categories && b.categories[0]) || b.category || '').toLowerCase();
        break;
      case 'stock':
        aValue = (a.active !== undefined ? a.active : a.inStock) ? 1 : 0;
        bValue = (b.active !== undefined ? b.active : b.inStock) ? 1 : 0;
        break;
      case 'featured':
        aValue = a.featured ? 1 : 0;
        bValue = b.featured ? 1 : 0;
        break;
      case 'template':
        aValue = a.templateType || '';
        bValue = b.templateType || '';
        break;
      default:
        aValue = a.name.toLowerCase();
        bValue = b.name.toLowerCase();
        break;
    }
    
    if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
    if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
    return 0;
  });

  const paginatedProducts = sortedProducts.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  if (loading) {
    return (
  <Container maxWidth={false} sx={{ py: 4, textAlign: 'center', px: { xs: 1, sm: 2, md: 4 } }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
  <Container maxWidth={false} sx={{ py: 4, px: { xs: 1, sm: 2, md: 4 } }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
        <Typography variant="h4" gutterBottom>Product Management</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenDialog()} size="large">Add Product</Button>
      </Box>

  {/* Products list */}
  <Box>
          {/* Search and Filter Controls */}
          <Paper sx={{ p: 2, mb: 3 }}>
            <Box sx={{ display: 'flex', flex: 1, gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
              {/* Search Field */}
              <TextField
                size="small"
                placeholder="Search products by name, SKU, description, or category..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                sx={{ minWidth: 300, flexGrow: 1 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      {searchQuery && searchQuery !== debouncedSearchQuery ? (
                        <CircularProgress size={16} />
                      ) : (
                        <Search color="action" />
                      )}
                    </InputAdornment>
                  ),
                }}
              />
              
              {/* Filters */}
              <Stack direction="row" spacing={2} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
                <FormControl size="small" sx={{ minWidth: 120 }}>
                  <InputLabel>Category</InputLabel>
                  <Select
                    value={categoryFilter}
                    label="Category"
                    onChange={(e) => {
                      setCategoryFilter(e.target.value);
                      setPage(0);
                    }}
                  >
                    <MenuItem value="all">All Categories</MenuItem>
                    {allCategories.map((category) => (
                      <MenuItem key={category.id} value={category.id}>
                        {getCategoryPath(category.id)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                
                <FormControl size="small" sx={{ minWidth: 120 }}>
                  <InputLabel>Stock</InputLabel>
                  <Select
                    value={stockFilter}
                    label="Stock"
                    onChange={(e) => {
                      setStockFilter(e.target.value);
                      setPage(0);
                    }}
                  >
                    <MenuItem value="all">All Stock</MenuItem>
                    <MenuItem value="inStock">In Stock</MenuItem>
                    <MenuItem value="outOfStock">Out of Stock</MenuItem>
                  </Select>
                </FormControl>
                
                <FormControl size="small" sx={{ minWidth: 120 }}>
                  <InputLabel>Template</InputLabel>
                  <Select
                    value={templateFilter}
                    label="Template"
                    onChange={(e) => {
                      setTemplateFilter(e.target.value);
                      setPage(0);
                    }}
                  >
                    <MenuItem value="all">All Templates</MenuItem>
                    <MenuItem value="standard">Standard</MenuItem>
                    <MenuItem value="text-only">Text Only</MenuItem>
                    <MenuItem value="single-image">Single Image</MenuItem>
                    <MenuItem value="multi-image">Multi Image</MenuItem>
                    <MenuItem value="image-and-text">Image & Text</MenuItem>
                  </Select>
                </FormControl>
                
                <FormControl size="small" sx={{ minWidth: 120 }}>
                  <InputLabel>Sort By</InputLabel>
                  <Select
                    value={sortBy}
                    label="Sort By"
                    onChange={(e) => {
                      setSortBy(e.target.value);
                      setPage(0);
                    }}
                  >
                    <MenuItem value="name">Name</MenuItem>
                    <MenuItem value="sku">SKU</MenuItem>
                    <MenuItem value="price">Price</MenuItem>
                    <MenuItem value="category">Category</MenuItem>
                    <MenuItem value="stock">Stock Status</MenuItem>
                    <MenuItem value="template">Template Type</MenuItem>
                  </Select>
                </FormControl>
                
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')}
                  startIcon={sortDirection === 'asc' ? <ArrowUpward /> : <ArrowDownward />}
                  sx={{ minWidth: 40 }}
                >
                  {sortDirection.toUpperCase()}
                </Button>
              </Stack>
              
              <Button
                variant="outlined"
                startIcon={<Refresh />}
                onClick={loadProducts}
              >
                Refresh
              </Button>
            </Box>
            
            {/* Results Summary and Clear Filters */}
            <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Showing {sortedProducts.length} of {products.length} products
                {(debouncedSearchQuery || categoryFilter !== 'all' || stockFilter !== 'all' || templateFilter !== 'all') && (
                  <> (filtered)</>
                )}
              </Typography>
              
              {(debouncedSearchQuery || categoryFilter !== 'all' || stockFilter !== 'all' || templateFilter !== 'all' || sortBy !== 'name' || sortDirection !== 'asc') && (
                <Button
                  size="small"
                  onClick={() => {
                    setSearchQuery('');
                    setDebouncedSearchQuery('');
                    setCategoryFilter('all');
                    setStockFilter('all');
                    setTemplateFilter('all');
                    setSortBy('name');
                    setSortDirection('asc');
                    setPage(0);
                    setSelectedProducts([]);
                    setSelectAll(false);
                  }}
                  startIcon={<FilterList />}
                >
                  Clear All Filters
                </Button>
              )}
            </Box>
          </Paper>

          {sortedProducts.length === 0 ? (
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <Typography variant="h6" gutterBottom>
                {products.length === 0 ? 'No products found' : 'No products match your filters'}
              </Typography>
              <Typography variant="body2" color="text.secondary" mb={3}>
                {products.length === 0
                  ? 'Start by adding your first product'
                  : 'Try adjusting your search criteria or filters'}
              </Typography>
              {products.length === 0 && (
                <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenDialog()}>
                  Add Your First Product
                </Button>
              )}
            </Paper>
          ) : (
            <Box>
          {/* Bulk Actions Toolbar */}
          {selectedProducts.length > 0 && (
            <Paper sx={{ p: 2, mb: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography variant="body2" color="text.secondary">
                {selectedProducts.length} product(s) selected
              </Typography>
              <Button
                variant="text"
                size="small"
                onClick={() => {
                  setSelectedProducts([]);
                  setSelectAll(false);
                }}
              >
                Clear Selection
              </Button>
              <Box sx={{ flexGrow: 1 }} />
              <Button
                variant="outlined"
                size="small"
                onClick={() => handleBulkStockUpdate(true)}
                disabled={bulkLoading}
              >
                Mark In Stock
              </Button>
              <Button
                variant="outlined"
                size="small"
                onClick={() => handleBulkStockUpdate(false)}
                disabled={bulkLoading}
              >
                Mark Out of Stock
              </Button>
              <Button
                variant="outlined"
                color="error"
                size="small"
                onClick={handleBulkDelete}
                disabled={bulkLoading}
                startIcon={<DeleteIcon />}
              >
                Delete Selected
              </Button>
              {bulkLoading && <CircularProgress size={20} />}
            </Paper>
          )}
          
          <TableContainer component={Paper} sx={{ width: '100%' }}>
          <Table sx={{ width: '100%' }}>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    indeterminate={selectedProducts.length > 0 && selectedProducts.length < sortedProducts.length}
                    checked={sortedProducts.length > 0 && selectedProducts.length === sortedProducts.length}
                    onChange={handleSelectAll}
                  />
                </TableCell>
                <TableCell>Image</TableCell>
                <TableCell>
                  <TableSortLabel
                    active={sortBy === 'name'}
                    direction={sortBy === 'name' ? sortDirection : 'asc'}
                    onClick={() => handleSort('name')}
                  >
                    Name
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={sortBy === 'sku'}
                    direction={sortBy === 'sku' ? sortDirection : 'asc'}
                    onClick={() => handleSort('sku')}
                  >
                    SKU
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={sortBy === 'category'}
                    direction={sortBy === 'category' ? sortDirection : 'asc'}
                    onClick={() => handleSort('category')}
                  >
                    Category
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={sortBy === 'price'}
                    direction={sortBy === 'price' ? sortDirection : 'asc'}
                    onClick={() => handleSort('price')}
                  >
                    Price
                  </TableSortLabel>
                </TableCell>
                <TableCell>Shipping</TableCell>
                <TableCell>
                  <TableSortLabel
                    active={sortBy === 'template'}
                    direction={sortBy === 'template' ? sortDirection : 'asc'}
                    onClick={() => handleSort('template')}
                  >
                    Template
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={sortBy === 'stock'}
                    direction={sortBy === 'stock' ? sortDirection : 'asc'}
                    onClick={() => handleSort('stock')}
                  >
                    Stock
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={sortBy === 'featured'}
                    direction={sortBy === 'featured' ? sortDirection : 'asc'}
                    onClick={() => handleSort('featured')}
                  >
                    Featured
                  </TableSortLabel>
                </TableCell>
                <TableCell>Label</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedProducts.map((product) => (
                <TableRow 
                  key={product.docId || product.id}
                  sx={{ 
                    backgroundColor: selectedProducts.includes(product.docId || product.id) 
                      ? 'rgba(25, 118, 210, 0.08)' 
                      : 'transparent',
                    '&:hover': {
                      backgroundColor: selectedProducts.includes(product.docId || product.id)
                        ? 'rgba(25, 118, 210, 0.12)'
                        : 'rgba(0, 0, 0, 0.04)'
                    }
                  }}
                >
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={selectedProducts.includes(product.docId || product.id)}
                      onChange={() => handleSelectProduct(product.docId || product.id)}
                    />
                  </TableCell>
                  <TableCell>
                    <Box
                      component="img"
                      src={product.imageUrls?.[0] || '/placeholder-image.png'}
                      alt={product.name}
                      sx={{
                        width: 60,
                        height: 60,
                        objectFit: 'cover',
                        borderRadius: 1
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="subtitle2">
                      {product.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {product.description?.substring(0, 50)}...
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight="medium">
                      {product.sku || '-'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {product.categories && product.categories.length > 0 ? (
                        product.categories.map((categoryId) => (
                          <Chip 
                            key={categoryId}
                            label={getCategoryPath(categoryId)} 
                            size="small" 
                            variant="outlined"
                            sx={{ mb: 0.5 }}
                          />
                        ))
                      ) : product.category ? (
                        <Chip 
                          label={getCategoryName(product.category)} 
                          size="small" 
                          variant="outlined"
                        />
                      ) : (
                        <Chip 
                          label="No Category" 
                          size="small" 
                          variant="outlined"
                          color="error"
                        />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    {product.salePrice && parseFloat(product.salePrice) < parseFloat(product.price) ? (
                      <Box>
                        <Typography variant="caption" sx={{ textDecoration: 'line-through', color: 'text.secondary' }}>
                          ₹{product.price}
                        </Typography>
                        <Typography variant="subtitle2" color="error" fontWeight="bold">
                          ₹{product.salePrice}
                        </Typography>
                        <Typography variant="caption" color="success.main" display="block">
                          {Math.round((1 - parseFloat(product.salePrice) / parseFloat(product.price)) * 100)}% OFF
                        </Typography>
                      </Box>
                    ) : (
                      <Typography variant="subtitle2">
                        ₹{product.price}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" display="block">
                      📦 {product.shipping?.weight ? `${product.shipping.weight}g` : 'N/A'}
                    </Typography>
                    {product.shipping?.dimensions && (
                      product.shipping.dimensions.length || 
                      product.shipping.dimensions.width || 
                      product.shipping.dimensions.height
                    ) && (
                      <Typography variant="caption" display="block" color="text.secondary">
                        📏 {product.shipping.dimensions.length || 0} × {product.shipping.dimensions.width || 0} × {product.shipping.dimensions.height || 0} cm
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={product.templateType || 'standard'}
                      color={getTemplateTypeColor(product.templateType)}
                      size="small"
                    />
                    <Typography variant="caption" display="block">
                      {product.imageRequired}📷 {product.textRequired && '📝'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={(product.active !== undefined ? product.active : product.inStock) ? 'In Stock' : 'Out of Stock'}
                      color={(product.active !== undefined ? product.active : product.inStock) ? 'success' : 'error'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={product.featured ? 'Featured' : 'Regular'}
                      color={product.featured ? 'secondary' : 'default'}
                      size="small"
                      variant={product.featured ? 'filled' : 'outlined'}
                    />
                  </TableCell>
                  <TableCell>
                    {product.label && (
                      <Chip
                        label={product.label}
                        color="primary"
                        size="small"
                        variant="outlined"
                        sx={{ 
                          fontSize: '0.75rem',
                          height: 20,
                          fontWeight: 'bold'
                        }}
                      />
                    )}
                    {!product.label && (
                      <Typography variant="caption" color="text.secondary">
                        No Label
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <IconButton
                      onClick={() => handleOpenDialog(product)}
                      color="primary"
                      size="small"
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      onClick={() => handleDeleteProduct(product.docId)}
                      color="error"
                      size="small"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        
        <TablePagination
          component="div"
          count={sortedProducts.length}
          page={page}
          onPageChange={(e, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[5, 10, 25, 50, 100]}
          labelRowsPerPage="Products per page:"
          labelDisplayedRows={({ from, to, count }) => 
            `${from}–${to} of ${count !== -1 ? count : `more than ${to}`} products`
          }
          showFirstButton
          showLastButton
        />
            </Box>
          )}

      {/* Add/Edit Product Dialog */}
      <Dialog 
        open={openDialog} 
        onClose={handleCloseDialog} 
        maxWidth="md" 
        fullWidth
      >
        <DialogTitle>
          {editingProduct ? 'Edit Product' : 'Add New Product'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Product Name"
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                <TextField
                  fullWidth
                  label="SKU (Stock Keeping Unit)"
                  value={formData.sku}
                  onChange={(e) => handleInputChange('sku', e.target.value)}
                  placeholder="e.g., TSHRT-BLU-M"
                  helperText="Unique identifier for this product"
                />
                <Button
                  variant="outlined"
                  onClick={() => handleInputChange('sku', generateSKU(formData.name))}
                  disabled={!formData.name.trim()}
                  sx={{ mt: 0, height: 56, minWidth: 100 }}
                  size="medium"
                >
                  Generate
                </Button>
              </Box>
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth required>
                <InputLabel id="categories-label">Categories</InputLabel>
                <Select
                  labelId="categories-label"
                  multiple
                  value={formData.categories}
                  onChange={handleCategoryChange}
                  input={<OutlinedInput label="Categories" />}
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map((value) => (
                        <Chip 
                          key={value} 
                          label={getCategoryPath(value)} 
                          size="small"
                          sx={{ height: 24 }}
                        />
                      ))}
                    </Box>
                  )}
                >
                  {Array.isArray(allCategories) && allCategories.map((category) => (
                    <MenuItem key={category.id} value={category.id}>
                      <Checkbox checked={formData.categories.indexOf(category.id) > -1} />
                      <ListItemText 
                        primary={getCategoryPath(category.id)} 
                        secondary={category.parentId ? undefined : `${category.childCount || 0} subcategories`}
                      />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                multiline
                rows={3}
                value={formData.description}
                onChange={(e) => {
                  if (e.target.value.length <= 200) handleInputChange('description', e.target.value);
                }}
                helperText={`${formData.description.length}/200 characters`}
                inputProps={{ maxLength: 200 }}
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Short Description"
                value={formData.shortDescription}
                onChange={(e)=>handleInputChange('shortDescription', e.target.value)}
                helperText={`${formData.shortDescription.length} characters`}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="YouTube Video URL (Optional)"
                value={formData.youtubeUrl}
                onChange={(e)=>handleInputChange('youtubeUrl', e.target.value)}
                placeholder="https://www.youtube.com/watch?v=VIDEO_ID"
                helperText="Paste a YouTube video link to show on product details page."
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <div style={{ background: '#fff', borderRadius: 4, border: '1px solid #ccc' }}>
                <label style={{ fontSize: 14, color: '#555', marginBottom: 4, display: 'block' }}>
                  Long Description / Specifications
                </label>
                <ReactQuill
                  theme="snow"
                  value={formData.longDescription}
                  onChange={val => handleInputChange('longDescription', val)}
                  modules={{
                    toolbar: [
                      [{ 'header': [1, 2, false] }],
                      ['bold', 'italic', 'underline'],
                      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
                      [{ 'align': [] }],
                      ['clean']
                    ]
                  }}
                  style={{ minHeight: 120 }}
                />
                <div style={{ fontSize: 12, color: '#888', marginTop: 4 }}>
                  Visible in Product Info collapsible section
                </div>
              </div>
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Price"
                type="number"
                value={formData.price}
                onChange={(e) => handleInputChange('price', e.target.value)}
                required
                InputProps={{
                  startAdornment: '₹'
                }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Sale Price (Optional)"
                type="number"
                value={formData.salePrice}
                onChange={(e) => handleInputChange('salePrice', e.target.value)}
                helperText="Leave empty if no discount. Must be less than regular price."
                error={formData.salePrice && parseFloat(formData.salePrice) >= parseFloat(formData.price)}
                InputProps={{
                  startAdornment: '₹'
                }}
              />
              {formData.salePrice && parseFloat(formData.salePrice) < parseFloat(formData.price) && (
                <Typography variant="caption" color="success.main" sx={{ mt: 0.5, display: 'block' }}>
                  Discount: {Math.round((1 - parseFloat(formData.salePrice) / parseFloat(formData.price)) * 100)}% OFF
                </Typography>
              )}
            </Grid>
            <Grid item xs={12} md={4}>
              {/* Placeholder for spacing */}
            </Grid>
            
            {/* Shipping Information */}
            <Grid item xs={12}>
              <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
                📦 Shipping Information
              </Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="HSN/SAC Code (Optional)"
                value={formData.hsnSacCode || ''}
                onChange={e => handleInputChange('hsnSacCode', e.target.value)}
                helperText="GST HSN or SAC code for this product/service"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Weight (grams)"
                type="number"
                value={formData.shipping.weight}
                onChange={(e) => handleShippingChange('weight', e.target.value)}
                inputProps={{ min: 0, step: 0.1 }}
                helperText="Product weight in grams"
              />
            </Grid>
            <Grid item xs={12} md={8}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Dimensions (cm)
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={4}>
                  <TextField
                    fullWidth
                    label="Length"
                    type="number"
                    value={formData.shipping.dimensions.length}
                    onChange={(e) => handleDimensionChange('length', e.target.value)}
                    inputProps={{ min: 0, step: 0.1 }}
                  />
                </Grid>
                <Grid item xs={4}>
                  <TextField
                    fullWidth
                    label="Width"
                    type="number"
                    value={formData.shipping.dimensions.width}
                    onChange={(e) => handleDimensionChange('width', e.target.value)}
                    inputProps={{ min: 0, step: 0.1 }}
                  />
                </Grid>
                <Grid item xs={4}>
                  <TextField
                    fullWidth
                    label="Height"
                    type="number"
                    value={formData.shipping.dimensions.height}
                    onChange={(e) => handleDimensionChange('height', e.target.value)}
                    inputProps={{ min: 0, step: 0.1 }}
                  />
                </Grid>
              </Grid>
            </Grid>
            
            {/* Customization Options */}
            <Grid item xs={12}>
              <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
                🎨 Customization Options
              </Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Images Required"
                type="number"
                value={formData.imageRequired}
                onChange={(e) => handleInputChange('imageRequired', e.target.value)}
                inputProps={{ min: 0, max: 10 }}
              />
              <Typography variant="caption" color="textSecondary" sx={{ mt: 0.5, display: 'block' }}>
                {parseInt(formData.imageRequired) === 0 && 'No images required from customer'}
                {parseInt(formData.imageRequired) === 1 && 'Customer must upload 1 image'}
                {parseInt(formData.imageRequired) > 1 && `Customer must upload ${formData.imageRequired} images`}
              </Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>Template Type</InputLabel>
                <Select
                  value={formData.templateType}
                  onChange={(e) => handleInputChange('templateType', e.target.value)}
                  label="Template Type"
                >
                  {getValidTemplateTypes(formData.imageRequired, formData.textRequired).map(type => (
                    <MenuItem key={type} value={type}>
                      {type.split('-').map(word => 
                        word.charAt(0).toUpperCase() + word.slice(1)
                      ).join(' ')}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Typography variant="caption" color="textSecondary" sx={{ mt: 0.5, display: 'block' }}>
                {formData.templateType === 'standard' && 'Works with any image/text combination'}
                {formData.templateType === 'text-only' && 'Requires text input, no images'}
                {formData.templateType === 'single-image' && 'Requires exactly 1 image'}
                {formData.templateType === 'multi-image' && 'Requires 2+ images'}
                {formData.templateType === 'image-and-text' && 'Requires both image(s) and text'}
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Box>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.textRequired}
                      onChange={(e) => handleInputChange('textRequired', e.target.checked)}
                    />
                  }
                  label="Text Input Required"
                />
                <Typography variant="caption" color="textSecondary" sx={{ display: 'block', ml: 4 }}>
                  {formData.textRequired ? 'Customer must provide text input' : 'No text input required'}
                </Typography>
              </Box>
              
              <Box mt={2}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.inStock}
                      onChange={(e) => handleInputChange('inStock', e.target.checked)}
                    />
                  }
                  label="In Stock"
                />
              </Box>
              
              <Box mt={2}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.featured}
                      onChange={(e) => handleInputChange('featured', e.target.checked)}
                    />
                  }
                  label="Featured Product"
                />
                <Typography variant="caption" color="textSecondary" sx={{ display: 'block', ml: 4 }}>
                  {formData.featured ? 'Will appear in featured products section on homepage' : 'Regular product display'}
                </Typography>
              </Box>
              
              <Box mt={2}>
                <TextField
                  fullWidth
                  label="Product Label (Optional)"
                  value={formData.label}
                  onChange={(e) => handleInputChange('label', e.target.value)}
                  placeholder="e.g., New, Sale, Limited Edition, Hot"
                  helperText="Label will appear on product card (keep it short, 10 characters max)"
                  inputProps={{ maxLength: 10 }}
                />
              </Box>
              
              {/* Configuration Summary */}
              <Box mt={3} p={2} bgcolor="background.default" borderRadius={1} border="1px solid" borderColor="divider">
                <Typography variant="subtitle2" color="primary" gutterBottom>
                  📋 Product Configuration Summary
                </Typography>
                <Grid container spacing={1}>
                  <Grid item xs={6}>
                    <Typography variant="body2">
                      <strong>Template:</strong> {formData.templateType.split('-').map(word => 
                        word.charAt(0).toUpperCase() + word.slice(1)
                      ).join(' ')}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2">
                      <strong>Images:</strong> {formData.imageRequired} required
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2">
                      <strong>Text Input:</strong> {formData.textRequired ? 'Required' : 'Not required'}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2">
                      <strong>Weight:</strong> {formData.shipping.weight ? `${formData.shipping.weight}g` : 'Not specified'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="body2">
                      <strong>Dimensions:</strong> {
                        formData.shipping.dimensions.length || formData.shipping.dimensions.width || formData.shipping.dimensions.height
                          ? `${formData.shipping.dimensions.length || 0} × ${formData.shipping.dimensions.width || 0} × ${formData.shipping.dimensions.height || 0} cm`
                          : 'Not specified'
                      }
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="body2">
                      <strong>Customer Action:</strong> 
                      {parseInt(formData.imageRequired) === 0 && !formData.textRequired && ' Just order'}
                      {parseInt(formData.imageRequired) === 0 && formData.textRequired && ' Provide text'}
                      {parseInt(formData.imageRequired) > 0 && !formData.textRequired && ` Upload ${formData.imageRequired} image(s)`}
                      {parseInt(formData.imageRequired) > 0 && formData.textRequired && ` Upload ${formData.imageRequired} image(s) + text`}
                    </Typography>
                  </Grid>
                </Grid>
              </Box>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle1" gutterBottom>
                Product Images
              </Typography>
              
              {/* Existing Images Preview */}
              {previewImages.length > 0 && (
                <Box mb={2}>
                  <Typography variant="body2" color="textSecondary" gutterBottom>
                    Existing Images (first image is main):
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', marginBottom: 2 }}>
                    {previewImages.map((imageUrl, index) => (
                      <Box key={imageUrl} sx={{ position: 'relative' }}>
                        <img 
                          src={imageUrl} 
                          alt={`Product ${index + 1}`}
                          style={{
                            width: 80,
                            height: 80,
                            objectFit: 'cover',
                            borderRadius: 8,
                            border: '1px solid #ddd',
                            boxShadow: index === 0 ? '0 0 0 2px #1976d2' : undefined
                          }}
                        />
                        <IconButton
                          size="small"
                          onClick={() => handleRemoveExistingImage(index)}
                          sx={{
                            position: 'absolute',
                            top: -8,
                            right: -8,
                            bgcolor: 'error.main',
                            color: 'white',
                            '&:hover': { bgcolor: 'error.dark' }
                          }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                        {previewImages.length > 1 && (
                          <Box sx={{ position: 'absolute', bottom: -8, left: -8, display: 'flex', gap: 0.5 }}>
                            {index > 0 && (
                              <IconButton
                                size="small"
                                onClick={() => handleMoveImage(index, index - 1)}
                                sx={{
                                  bgcolor: 'primary.main',
                                  color: 'white',
                                  width: 20,
                                  height: 20,
                                  '&:hover': { bgcolor: 'primary.dark' }
                                }}
                              >
                                <ArrowUpward fontSize="small" />
                              </IconButton>
                            )}
                            {index < previewImages.length - 1 && (
                              <IconButton
                                size="small"
                                onClick={() => handleMoveImage(index, index + 1)}
                                sx={{
                                  bgcolor: 'primary.main',
                                  color: 'white',
                                  width: 20,
                                  height: 20,
                                  '&:hover': { bgcolor: 'primary.dark' }
                                }}
                              >
                                <ArrowDownward fontSize="small" />
                              </IconButton>
                            )}
                          </Box>
                        )}
                      </Box>
                    ))}
                  </Box>
                </Box>
              )}

              {/* File Upload Area */}
              <Paper
                variant="outlined"
                sx={{
                  p: 3,
                  textAlign: 'center',
                  bgcolor: 'background.default',
                  border: selectedFiles.length > 0 ? `2px dashed ${COLORS.primary.main}` : `2px dashed ${COLORS.divider || '#ccc'}`,
                  cursor: 'pointer',
                  '&:hover': {
                    bgcolor: 'action.hover'
                  }
                }}
                onClick={() => document.getElementById('file-upload').click()}
              >
                <input
                  id="file-upload"
                  type="file"
                  multiple
                  accept="image/*"
                  onChange={handleFileSelect}
                  style={{ display: 'none' }}
                />
                <CloudUploadIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                <Typography variant="h6" gutterBottom>
                  Upload Product Images
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Click to select multiple images or drag and drop
                </Typography>
                <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                  Supported formats: JPG, PNG, GIF (Max 10MB each)
                </Typography>
              </Paper>

              {/* Selected Files Preview */}
              {selectedFiles.length > 0 && (
                <Box mt={2}>
                  <Typography variant="body2" gutterBottom>
                    Selected Files ({selectedFiles.length}):
                  </Typography>
                  <Box display="flex" gap={1} flexWrap="wrap">
                    {selectedFiles.map((file, index) => (
                      <Box key={index} position="relative">
                        <img 
                          src={URL.createObjectURL(file)}
                          alt={file.name}
                          style={{
                            width: 80,
                            height: 80,
                            objectFit: 'cover',
                            borderRadius: 8,
                            border: '1px solid #ddd'
                          }}
                        />
                        <IconButton
                          size="small"
                          onClick={() => handleRemoveSelectedFile(index)}
                          sx={{
                            position: 'absolute',
                            top: -8,
                            right: -8,
                            bgcolor: 'error.main',
                            color: 'white',
                            '&:hover': { bgcolor: 'error.dark' }
                          }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    ))}
                  </Box>
                </Box>
              )}

              {/* Upload Progress */}
              {uploadingImages && (
                <Box mt={2}>
                  <Typography variant="body2" gutterBottom>
                    Uploading images... {Math.round(uploadProgress)}%
                  </Typography>
                  <LinearProgress variant="determinate" value={uploadProgress} />
                </Box>
              )}
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>
            Cancel
          </Button>
          <Button
            onClick={handleSaveProduct}
            variant="contained"
            disabled={!formData.name || (!formData.category && formData.categories.length === 0) || !formData.price}
          >
            {editingProduct ? 'Update Product' : 'Create Product'}
          </Button>
        </DialogActions>
      </Dialog>

  {/* Floating Action Button for mobile */}
  <Fab
        color="primary"
        aria-label="add"
        onClick={() => handleOpenDialog()}
        sx={{
          position: 'fixed',
          bottom: 16,
          right: 16,
          display: { xs: 'flex', md: 'none' }
        }}
      >
        <AddIcon />
      </Fab>
    </Box>
  </Container>
  );
};

export default AdminProducts;
